import { trpcClient } from '@client/trpc/client';
import { createLogger } from '@client/utils/logger';
import { I18N_NAMESPACES, type I18nNamespace } from '@shared/constants';
import type { BackendModule, ReadCallback } from 'i18next';

const log = createLogger('i18n');

const pendingTranslationLoads = new Map<string, Promise<Record<string, string>>>();

function getTranslationLoadKey(language: string, namespace: I18nNamespace) {
    return `${language}:${namespace}`;
}

function isUnauthorizedError(error: unknown): boolean {
    return (
        error instanceof Error &&
        'data' in error &&
        (error as { data?: { code?: string } }).data?.code === 'UNAUTHORIZED'
    );
}

function loadTranslations(language: string, namespace: I18nNamespace) {
    return trpcClient.appText.getTranslations.query({ locale: language, namespace });
}

async function clearStoredAuthTokenIfPresent() {
    const { useAuthStore } = await import('@client/stores/AuthStore');
    const authState = useAuthStore.getState();

    if (!authState.getAuthToken()) {
        return false;
    }

    authState.clearAuthToken();
    return true;
}

export const trpcBackend: BackendModule = {
    type: 'backend',

    init(_services, _backendOptions, _i18nextOptions) {},

    read(language: string, namespace: string, callback: ReadCallback) {
        if (!I18N_NAMESPACES.includes(namespace as I18nNamespace)) {
            log.error(`Invalid i18n namespace: ${namespace}`);
            callback(new Error(`Invalid namespace: ${namespace}`), null);
            return;
        }

        const normalizedNamespace = namespace as I18nNamespace;
        const key = getTranslationLoadKey(language, normalizedNamespace);

        let translationLoad = pendingTranslationLoads.get(key);
        if (!translationLoad) {
            translationLoad = loadTranslations(language, normalizedNamespace)
                .catch(async (error) => {
                    // A stale/invalid token in localStorage would otherwise block the
                    // public translations endpoint during bootstrap. Clear it and retry.
                    if (isUnauthorizedError(error) && (await clearStoredAuthTokenIfPresent())) {
                        log.warn('Translations request rejected as unauthorized — clearing stored token and retrying');
                        return loadTranslations(language, normalizedNamespace);
                    }
                    throw error;
                })
                .finally(() => {
                    pendingTranslationLoads.delete(key);
                });

            pendingTranslationLoads.set(key, translationLoad);
        }

        translationLoad
            .then((data) => {
                callback(null, data);
            })
            .catch((error) => {
                log.error('Failed to load translations:', error);
                callback(error, null);
            });
    },
};
